import { memo, useCallback } from "react";
import { Handle, useReactFlow, type NodeProps } from "@xyflow/react";
import { ListChecks, Plus, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MembershipNodeData } from "@/types/decision-graph";
import {
  getDualHandleOffsetStyle,
  getHandlePositions,
  handleOffsetStyle,
} from "../utils/handle-positions";

export const MembershipNode = memo(function MembershipNode({
  id,
  data,
  selected,
}: NodeProps) {
  const d = data as MembershipNodeData;
  const { setNodes } = useReactFlow();

  const handleOperatorChange = useCallback(
    (operator: string) => {
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, operator, label: operator } }
            : node
        )
      );
    },
    [id, setNodes]
  );

  const handleAddSetElement = useCallback(() => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id
          ? {
              ...node,
              data: { ...node.data, setSize: (d.setSize || 0) + 1 },
            }
          : node
      )
    );
  }, [id, d.setSize, setNodes]);

  const handleRemoveSetElement = useCallback(() => {
    if ((d.setSize || 0) > 0) {
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, setSize: d.setSize - 1 } }
            : node
        )
      );
    }
  }, [id, d.setSize, setNodes]);

  const isIn = d.operator === "IN";
  const setSize = d.setSize || 0;
  const { isHorizontal, inputPosition, outputPosition } = getHandlePositions(
    d.layoutDirection
  );

  const getSetHandleOffset = (index: number): string => {
    // Mirror dagre sibling order: later indices sit closer to the near edge
    const startPercent = 50;
    const spacing = 20;
    const maxIndex = Math.max(setSize - 1, 0);
    const visualIndex = maxIndex - index;
    return `${startPercent + visualIndex * spacing}%`;
  };

  return (
    <div
      className={cn(
        "px-3 py-2 rounded-lg border-2 bg-white shadow-sm min-w-[100px]",
        "transition-all duration-150",
        selected
          ? "border-blue-500 shadow-md ring-2 ring-blue-200"
          : "border-teal-200 hover:border-teal-300"
      )}
      style={{ minHeight: `${60 + setSize * 24}px` }}
    >
      <Handle
        type="target"
        position={inputPosition}
        id="operand"
        style={{
          ...getDualHandleOffsetStyle(isHorizontal, "second"),
          background: "#6366f1",
          width: 8,
          height: 8,
        }}
      />

      {Array.from({ length: setSize }).map((_, index) => (
        <Handle
          key={`set-${index}`}
          type="target"
          position={inputPosition}
          id={`set-${index}`}
          style={{
            ...handleOffsetStyle(isHorizontal, getSetHandleOffset(index)),
            background: "#14b8a6",
            width: 6,
            height: 6,
          }}
        />
      ))}

      <div className="flex items-center gap-1.5 mb-2">
        <ListChecks size={14} className="text-teal-500" />
        <span className="text-[10px] text-slate-500 font-medium">Set</span>
      </div>

      <Select value={d.operator} onValueChange={handleOperatorChange}>
        <SelectTrigger
          className={cn(
            "h-7 w-full text-xs font-semibold text-center justify-center",
            isIn ? "text-teal-700" : "text-rose-700"
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="IN">
            <span className="font-semibold text-teal-700">IN</span>
          </SelectItem>
          <SelectItem value="NOT_IN">
            <span className="font-semibold text-rose-700">NOT IN</span>
          </SelectItem>
        </SelectContent>
      </Select>

      <div className="text-[9px] text-slate-400 mt-1">
        <div>value ↓</div>
        {setSize > 0 && <div className="mt-0.5">set [{setSize}]</div>}
      </div>

      <div className="flex gap-1 mt-2">
        <Button
          variant="outline"
          size="sm"
          className="h-5 w-5 p-0"
          onClick={handleAddSetElement}
          title="Add set element"
        >
          <Plus size={10} />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-5 w-5 p-0"
          onClick={handleRemoveSetElement}
          disabled={setSize === 0}
          title="Remove set element"
        >
          <Minus size={10} />
        </Button>
      </div>

      <Handle
        type="source"
        position={outputPosition}
        id="result"
        style={{ background: "#22c55e", width: 8, height: 8 }}
      />
    </div>
  );
});
